import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import CateringListing from '../components/traiteur/CateringListing';
import { getCateringFormulas, createCateringBooking } from '../services/cateringApi';
import { useFavoriteAuth } from '../hooks/useFavoriteAuth';
import LoginPopup from '../components/LoginPopup';
import { CATERING_OCCASIONS, DIETARY_OPTIONS } from '../constants/catering';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const PRICE_LOCALES = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE' };

export default function Traiteur() {
  const { t, i18n } = useTranslation();
  const formatPrice = (n) => Number(n || 0).toLocaleString(PRICE_LOCALES[i18n.language] || 'fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  // Le contenu des formules est saisi en français en base : traduction par slug, sinon texte d'origine
  const formulaName = (f) => t(`catering.formulaContent.${f.slug}.name`, { defaultValue: f.name });
  const formulaIncludes = (f) => {
    const items = t(`catering.formulaContent.${f.slug}.includes`, { returnObjects: true, defaultValue: null });
    return Array.isArray(items) ? items : f.includes || [];
  };
  const [searchParams] = useSearchParams();
  const { isAuthenticated, showLoginPopup, handleReviewAction, handleLoginSuccess, closeLoginPopup } = useFavoriteAuth();
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [occasion, setOccasion] = useState('birthday');
  const [guests, setGuests] = useState(40);
  const [selectedFormula, setSelectedFormula] = useState(null); // slug — pilote aussi le filtre de l'annuaire des cuisiniers
  const [dietary, setDietary] = useState([]);
  const [form, setForm] = useState({ email: '', date: '', time: '', location: '' });
  const [message, setMessage] = useState('');
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(false);
  const [selectedDishes, setSelectedDishes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCateringFormulas();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setFormulas(list);
        const fromUrl = searchParams.get('formula');
        const preselected = fromUrl && list.find((f) => f.slug === fromUrl);
        const featured = list.find((f) => f.is_featured);
        setSelectedFormula((preselected || featured || list[0])?.slug || null);
        if (preselected) {
          setTimeout(() => scrollTo('cuisiniers'), 150);
        }
      } catch (e) {
        console.error('Erreur chargement formules traiteur:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFormula = formulas.find((f) => f.slug === selectedFormula) || null;
  const menuOptions = activeFormula?.menu_options || [];
  const selectableDishes = menuOptions.filter((d) => d.selectable);
  const includedMenuItems = menuOptions.filter((d) => !d.selectable);
  const choiceCount = activeFormula?.menu_choice_count || 0;
  const minGuests = activeFormula?.min_guests || 10;
  const maxGuests = activeFormula?.max_guests || 200;

  // Chiffres du hero calculés à partir des formules publiées
  const pricedFormulas = formulas.filter((f) => !f.is_custom_price && Number(f.price_per_person) > 0);
  const heroMinPrice = pricedFormulas.length ? Math.min(...pricedFormulas.map((f) => Number(f.price_per_person))) : 18;
  const heroMinGuests = formulas.length ? Math.min(...formulas.map((f) => f.min_guests || 10)) : 10;
  const heroMaxGuests = formulas.length ? Math.max(...formulas.map((f) => f.max_guests || 200)) : 200;

  useEffect(() => {
    setSelectedDishes([]);
    // Le nombre de convives reste dans la plage acceptée par la formule (sinon l'API refuse la demande)
    setGuests((g) => Math.min(Math.max(g, minGuests), maxGuests));
  }, [selectedFormula, minGuests, maxGuests]);

  const toggleDish = (key) => {
    setSelectedDishes((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (choiceCount > 0 && prev.length >= choiceCount) return prev;
      return [...prev, key];
    });
  };

  const estimate = useMemo(() => {
    if (!activeFormula || activeFormula.is_custom_price) return null;
    const total = Number(activeFormula.price_per_person) * guests;
    const depositPercentage = 30;
    return { total, deposit: total * (depositPercentage / 100), depositPercentage };
  }, [activeFormula, guests]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const browseCooksInstead = () => {
    if (!activeFormula) {
      setNote(t('catering.quote.chooseFormula'));
      setNoteError(true);
      return;
    }
    setNoteError(false);
    setNote(t('catering.quote.redirectNote', { formula: formulaName(activeFormula), guests }));
    scrollTo('cuisiniers');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNoteError(false);

    // La demande est rattachée au compte client : connexion obligatoire
    if (!isAuthenticated) {
      setNote(t('catering.quote.loginRequired'));
      setNoteError(true);
      handleReviewAction('submitCateringQuote', () => setNote(''));
      return;
    }

    if (!activeFormula) {
      setNote(t('catering.quote.chooseFormula'));
      setNoteError(true);
      return;
    }
    if (!form.date) {
      setNote(t('catering.quote.missingDate'));
      setNoteError(true);
      return;
    }
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) {
      setNote(t('catering.quote.missingEmail'));
      setNoteError(true);
      return;
    }
    if (choiceCount > 0 && selectedDishes.length < choiceCount) {
      setNote(t('catering.quote.missingDishes', { count: choiceCount }));
      setNoteError(true);
      return;
    }

    setSubmitting(true);
    try {
      await createCateringBooking({
        formula_id: activeFormula.id,
        event_date: form.date,
        event_time: form.time ? `${form.time}:00` : '12:00:00',
        guest_count: guests,
        occasion,
        contact_email: form.email,
        delivery_address: form.location || undefined,
        special_requests: message || undefined,
        dietary_requirements: dietary.length > 0 ? dietary : undefined,
        selected_dishes: selectedDishes.length > 0 ? selectedDishes : undefined,
      });
      setNoteError(false);
      setNote(t('catering.quote.successNote', { formula: formulaName(activeFormula), guests }));
    } catch (err) {
      console.error('Erreur lors de la demande de devis traiteur:', err);
      setNoteError(true);
      // Les refus métier de l'API (convives, délai, aucun cuisinier) sont explicites : on les affiche
      setNote(err?.message || t('catering.quote.errorNote'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <NavBar />

      {/* Hero */}
      <section style={{ background: PRIMARY, color: '#FBF9D9', position: 'relative', overflow: 'hidden' }}>
        <span style={{ position: 'absolute', right: '-160px', top: '-180px', width: '460px', height: '460px', borderRadius: '999px', background: '#72B744', opacity: 0.22 }} />
        <div style={{ position: 'relative', maxWidth: '1280px', margin: '0 auto', padding: 'clamp(100px, 12vw, 140px) clamp(16px, 3vw, 40px) clamp(40px, 5vw, 76px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 'clamp(28px, 4vw, 56px)', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '22px', alignItems: 'flex-start' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', background: 'rgba(251,249,217,0.14)', fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '9px 18px', borderRadius: '999px', color: '#FFC717' }}>
              {t('catering.hero.badge')}
            </span>
            <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(36px, 5.4vw, 72px)', lineHeight: 0.97, letterSpacing: '-0.025em', maxWidth: '18ch' }}>
              {t('catering.hero.title')}
            </h1>
            <p style={{ margin: 0, fontSize: 'clamp(16px, 1.35vw, 20px)', lineHeight: 1.6, maxWidth: '48ch', color: 'rgba(251,249,217,0.84)' }}>
              {t('catering.hero.subtitle')}
            </p>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => scrollTo('devis')}
                style={{ background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', padding: '16px 32px', borderRadius: '999px', border: 'none', cursor: 'pointer' }}
              >
                {t('catering.hero.ctaQuote')}
              </button>
              <button
                type="button"
                onClick={() => scrollTo('formules')}
                style={{ border: '2px solid rgba(251,249,217,0.4)', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', padding: '14px 28px', borderRadius: '999px', background: 'transparent', cursor: 'pointer' }}
              >
                {t('catering.hero.ctaFormulas')}
              </button>
            </div>
            <div className="flex flex-wrap gap-6" style={{ marginTop: '6px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: 'rgba(251,249,217,0.76)' }}>
              <span><strong style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '26px', color: '#FBF9D9' }}>24 h</strong>{t('catering.hero.statResponse')}</span>
              <span><strong style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '26px', color: '#FBF9D9' }}>{heroMinGuests}–{heroMaxGuests}</strong>{t('catering.hero.statGuests')}</span>
              <span><strong style={{ display: 'block', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '26px', color: '#FBF9D9' }}>{heroMinPrice.toLocaleString(PRICE_LOCALES[i18n.language] || 'fr-FR')} €</strong>{t('catering.hero.statFrom')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Formules */}
      <section id="formules" style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(44px, 6vw, 80px) clamp(16px, 3vw, 40px) clamp(24px, 3vw, 40px)', display: 'flex', flexDirection: 'column', gap: '26px' }}>
        <div className="flex items-end gap-5 flex-wrap">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: TERRA }}>{t('catering.formulas.eyebrow')}</span>
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.02em', maxWidth: '24ch' }}>{t('catering.formulas.title')}</h2>
          </div>
          <span style={{ marginLeft: 'auto', fontFamily: 'Archivo, sans-serif', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>{t('catering.formulas.priceNote')}</span>
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '18px' }}>
            {[0, 1, 2].map((i) => <div key={i} style={{ height: '320px', borderRadius: '32px', background: '#FBF9D9' }} className="animate-pulse" />)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '18px' }}>
            {formulas.map((f) => (
              <article
                key={f.id}
                style={{
                  borderRadius: '32px', padding: '28px 26px', display: 'flex', flexDirection: 'column', gap: '14px',
                  background: f.is_featured ? '#FBF9D9' : '#FCFBF5',
                  border: selectedFormula === f.slug ? `2px solid ${PRIMARY}` : f.is_featured ? `2px solid ${TERRA}` : '1px solid rgba(5,16,4,0.12)',
                  boxShadow: f.is_featured ? '0 14px 32px rgba(5,16,4,0.1)' : 'none',
                }}
              >
                <span className="flex items-center gap-2.5">
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>{t('catering.formulas.number', { n: String(f.display_order).padStart(2, '0') })}</span>
                  {f.is_featured && (
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#FFC717', color: '#051004', padding: '5px 12px', borderRadius: '999px' }}>
                      {t('catering.formulas.featuredBadge')}
                    </span>
                  )}
                </span>
                <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '26px', lineHeight: 1.1 }}>{formulaName(f)}</h3>
                <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '34px', letterSpacing: '-0.02em' }}>
                  {f.is_custom_price ? t('catering.formulas.customPrice') : formatPrice(f.price_per_person)}
                </span>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '9px', fontSize: '14.5px', lineHeight: 1.5 }}>
                  {formulaIncludes(f).map((item, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span style={{ color: '#72B744', fontWeight: 700 }}>✓</span>{item}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => { setSelectedFormula(f.slug); scrollTo('cuisiniers'); }}
                  style={{
                    marginTop: 'auto', textAlign: 'center', cursor: 'pointer', border: 'none', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px',
                    padding: '13px 24px', borderRadius: '999px',
                    background: f.is_featured ? PRIMARY : '#FBF9D9', color: f.is_featured ? '#FBF9D9' : PRIMARY,
                  }}
                >
                  {selectedFormula === f.slug ? t('catering.formulas.ctaViewSelected') : t('catering.formulas.ctaView')}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Tous les cuisiniers traiteurs */}
      <section id="cuisiniers" style={{ background: '#FBF9D9', padding: 'clamp(40px, 5vw, 76px) 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 40px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: TERRA }}>{t('catering.cooks.eyebrow')}</span>
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.02em', maxWidth: '24ch' }}>
              {activeFormula ? t('catering.cooks.titleFiltered', { formula: formulaName(activeFormula) }) : t('catering.cooks.titleDefault')}
            </h2>
            <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.6, maxWidth: '60ch', color: 'rgba(5,16,4,0.7)' }}>
              {activeFormula ? t('catering.cooks.subtitleFiltered') : t('catering.cooks.subtitleDefault')}
            </p>
            {activeFormula && (
              <button
                type="button"
                onClick={() => setSelectedFormula(null)}
                style={{ alignSelf: 'flex-start', border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: PRIMARY, textDecoration: 'underline' }}
              >
                {t('catering.cooks.clearFilter')}
              </button>
            )}
          </div>
          <CateringListing formulaId={activeFormula?.id} />
        </div>
      </section>

      {/* Comment ça se passe */}
      <section style={{ background: '#FBF9D9', padding: 'clamp(40px, 5vw, 76px) 0' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 40px)', display: 'flex', flexDirection: 'column', gap: '30px' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.02em', maxWidth: '22ch' }}>{t('catering.howItWorks.title')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: '18px' }}>
            {[
              { n: '01', bg: TERRA, fg: '#FCFBF5', title: t('catering.howItWorks.step1Title'), text: t('catering.howItWorks.step1Text') },
              { n: '02', bg: '#72B744', fg: '#051004', title: t('catering.howItWorks.step2Title'), text: t('catering.howItWorks.step2Text') },
              { n: '03', bg: '#FFC717', fg: '#051004', title: t('catering.howItWorks.step3Title'), text: t('catering.howItWorks.step3Text') },
              { n: '04', bg: PRIMARY, fg: '#FBF9D9', title: t('catering.howItWorks.step4Title'), text: t('catering.howItWorks.step4Text') },
            ].map((step) => (
              <div key={step.n} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span style={{ width: '52px', height: '52px', borderRadius: '999px', background: step.bg, color: step.fg, display: 'grid', placeItems: 'center', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '21px' }}>{step.n}</span>
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>{step.title}</strong>
                <span style={{ fontSize: '14.5px', lineHeight: 1.6, color: 'rgba(5,16,4,0.76)' }}>{step.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Devis */}
      <section id="devis" style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(44px, 6vw, 80px) clamp(16px, 3vw, 40px)' }}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]" style={{ gap: 'clamp(24px, 4vw, 48px)', alignItems: 'start' }}>
          <form onSubmit={handleSubmit} style={{ background: '#FBF9D9', borderRadius: 'clamp(28px, 3.5vw, 44px)', padding: 'clamp(26px, 3.5vw, 44px)', display: 'flex', flexDirection: 'column', gap: '22px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: TERRA }}>{t('catering.quote.eyebrow')}</span>
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(24px, 3vw, 38px)', lineHeight: 1.06, letterSpacing: '-0.02em' }}>{t('catering.quote.title')}</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.email')}</span>
                <input type="email" required placeholder="vous@exemple.com" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '13px 20px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FCFBF5', color: '#051004', outline: 'none' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.date')}</span>
                <input type="date" required min={new Date().toISOString().split('T')[0]} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '12px 20px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FCFBF5', color: '#051004', outline: 'none' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.time')}</span>
                <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '12px 20px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FCFBF5', color: '#051004', outline: 'none' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.location')}</span>
                <input type="text" placeholder={t('catering.quote.locationPlaceholder')} value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '999px', padding: '13px 20px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FCFBF5', color: '#051004', outline: 'none' }} />
              </label>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.occasion')}</span>
              <div className="flex flex-wrap gap-2">
                {CATERING_OCCASIONS.map(({ id: key }) => {
                  const active = occasion === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setOccasion(key)}
                      style={{ cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '14px', padding: '10px 18px', borderRadius: '999px', border: active ? 'none' : '1px solid rgba(5,16,4,0.14)', background: active ? PRIMARY : '#FCFBF5', color: active ? '#FBF9D9' : '#051004' }}
                    >
                      {t(`catering.quote.eventTypes.${key}`)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <span className="flex items-baseline gap-2.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>
                {t('catering.quote.guests')}
                <strong style={{ marginLeft: 'auto', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px', letterSpacing: 0, textTransform: 'none' }}>{guests} {t('catering.quote.guestsUnit')}</strong>
              </span>
              <input type="range" min={minGuests} max={maxGuests} step={5} value={guests} onChange={(e) => setGuests(Number(e.target.value))} style={{ width: '100%', accentColor: TERRA }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.formula')}</span>
              <div className="flex flex-wrap gap-2">
                {formulas.map((f) => {
                  const active = selectedFormula === f.slug;
                  return (
                    <button
                      key={f.slug}
                      type="button"
                      onClick={() => setSelectedFormula(f.slug)}
                      style={{ cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '14px', padding: '10px 18px', borderRadius: '999px', border: active ? 'none' : '1px solid rgba(5,16,4,0.14)', background: active ? PRIMARY : '#FCFBF5', color: active ? '#FBF9D9' : '#051004' }}
                    >
                      {formulaName(f)} · {f.is_custom_price ? t('catering.formulas.customPrice') : formatPrice(f.price_per_person)}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectableDishes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
                <span className="flex items-baseline gap-2.5 flex-wrap" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>
                  {t('catering.quote.menu.title')}
                  <span style={{ textTransform: 'none', fontWeight: 500, letterSpacing: 0 }}>
                    {t('catering.quote.menu.subtitle', { count: choiceCount, picked: selectedDishes.length })}
                  </span>
                </span>
                {includedMenuItems.filter((it) => it.category === 'entree').map((it) => (
                  <div key={it.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '18px', padding: '12px 18px' }}>
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>{it.name}</strong>
                      {it.description && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.62)' }}>{it.description}</span>}
                    </span>
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: PRIMARY }}>{t('catering.quote.menu.includedTag')}</span>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  {selectableDishes.map((dish) => {
                    const active = selectedDishes.includes(dish.key);
                    return (
                      <button
                        key={dish.key}
                        type="button"
                        onClick={() => toggleDish(dish.key)}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', textAlign: 'left', cursor: 'pointer',
                          fontFamily: 'Poppins, sans-serif', fontSize: '14px', padding: '10px 18px', borderRadius: '18px', minWidth: '160px',
                          border: active ? `2px solid ${PRIMARY}` : '1px solid rgba(5,16,4,0.14)',
                          background: active ? '#FBF9D9' : '#FCFBF5', color: '#051004',
                        }}
                      >
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600 }}>{dish.name}</strong>
                        {dish.description && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.62)' }}>{dish.description}</span>}
                      </button>
                    );
                  })}
                </div>
                {includedMenuItems.filter((it) => it.category !== 'entree').map((it) => (
                  <div key={it.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '18px', padding: '12px 18px' }}>
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>{it.name}</strong>
                      {it.description && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.62)' }}>{it.description}</span>}
                    </span>
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, color: PRIMARY }}>{t('catering.quote.menu.includedTag')}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.dietary')}</span>
              <div className="flex flex-wrap gap-4.5" style={{ fontSize: '14.5px' }}>
                {DIETARY_OPTIONS.map(({ id: key }) => (
                  <label key={key} className="flex items-center gap-2.5" style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={dietary.includes(key)} onChange={(e) => setDietary((d) => (e.target.checked ? [...d, key] : d.filter((k) => k !== key)))} style={{ width: '17px', height: '17px', accentColor: PRIMARY }} />
                    {t(`catering.dietaryOptions.${key}`)}
                  </label>
                ))}
              </div>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('catering.quote.message')}</span>
              <textarea rows={4} placeholder={t('catering.quote.messagePlaceholder')} value={message} onChange={(e) => setMessage(e.target.value)} style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '22px', padding: '14px 20px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FCFBF5', color: '#051004', resize: 'vertical', outline: 'none' }} />
            </label>

            <div style={{ borderTop: '1px solid rgba(5,16,4,0.12)', paddingTop: '20px', display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.66)' }}>{t('catering.quote.estimate')}</span>
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '30px', letterSpacing: '-0.02em' }}>{estimate ? formatPrice(estimate.total) : '—'}</strong>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.55)', maxWidth: '32ch' }}>{t('catering.quote.formulaPriceNote')}</span>
              </span>
              <button
                type="submit"
                disabled={submitting}
                style={{ marginLeft: 'auto', border: 'none', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1, background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', padding: '16px 32px', borderRadius: '999px' }}
              >
                {submitting ? t('catering.quote.submitting') : t('catering.quote.submitBooking')}
              </button>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2.5">
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: noteError ? TERRA : PRIMARY, minHeight: '20px' }}>{note}</span>
              <button
                type="button"
                onClick={browseCooksInstead}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: PRIMARY, textDecoration: 'underline' }}
              >
                {t('catering.quote.browseCooksInstead')}
              </button>
            </div>
          </form>

          <aside style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'sticky', top: '92px' }}>
            <div style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '32px', padding: '26px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>{t('catering.quote.included.title')}</h3>
              {[
                t('catering.quote.included.item1'),
                t('catering.quote.included.item2'),
                t('catering.quote.included.item3'),
                t('catering.quote.included.item4'),
              ].map((line) => (
                <span key={line} className="flex gap-2.5" style={{ fontSize: '14.5px', lineHeight: 1.55 }}>
                  <span style={{ color: '#72B744', fontWeight: 700 }}>✓</span>{line}
                </span>
              ))}
            </div>

            <blockquote style={{ margin: 0, background: '#FBF9D9', borderRadius: '32px', padding: '26px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: PRIMARY }}>★★★★★</span>
              <p style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontSize: '18px', lineHeight: 1.45 }}>{t('catering.quote.testimonial.quote')}</p>
              <footer className="flex items-center gap-2.5" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.66)' }}>
                <span style={{ width: '32px', height: '32px', borderRadius: '999px', background: '#72B744' }} />{t('catering.quote.testimonial.author')}
              </footer>
            </blockquote>

            <div style={{ background: PRIMARY, color: '#FBF9D9', borderRadius: '32px', padding: '26px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>{t('catering.quote.contact.title')}</h3>
              <span style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'rgba(251,249,217,0.82)' }}>{t('catering.quote.contact.subtitle')}</span>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px', letterSpacing: '-0.02em', color: '#FFC717' }}>+49 176 86660296</strong>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 clamp(16px, 3vw, 40px) clamp(48px, 6vw, 88px)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(24px, 3vw, 38px)', letterSpacing: '-0.02em' }}>{t('catering.faq.title')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '16px' }}>
          {[
            [t('catering.faq.q1'), t('catering.faq.a1')],
            [t('catering.faq.q2'), t('catering.faq.a2')],
            [t('catering.faq.q3'), t('catering.faq.a3')],
            [t('catering.faq.q4'), t('catering.faq.a4')],
          ].map(([q, a]) => (
            <div key={q} style={{ background: '#FBF9D9', borderRadius: '28px', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>{q}</strong>
              <span style={{ fontSize: '14.5px', lineHeight: 1.6, color: 'rgba(5,16,4,0.78)' }}>{a}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => scrollTo('cuisiniers')}
          style={{ alignSelf: 'flex-start', border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: PRIMARY }}
        >
          {t('catering.faq.browseCta')}
        </button>
      </section>

      {showLoginPopup && (
        <LoginPopup onClose={closeLoginPopup} onLoginSuccess={handleLoginSuccess} />
      )}

      <Footer />
    </>
  );
}
